<template>
  <v-container
    class="example-full pa-0"
    fluid
  >
    <v-overlay v-show="$refs.upload && $refs.upload.dropActive">
      <h1 class="display-1">
        {{ $t("mediacenter.drop-upload") }}
      </h1>
    </v-overlay>
    <div class="upload">
      <v-simple-table
        height="calc(85vh - 240px)"
        fixed-header
      >
        <thead>
          <tr>
            <th class="text-center">
              {{ $t("mediacenter.preview") }}
            </th>
            <th class="text-left">
              {{ $t("mediacenter.filename") }}
            </th>
            <th class="text-center">
              {{ $t("mediacenter.size") }}
            </th>
            <th class="text-center">
              {{ $t("mediacenter.status") }}
            </th>
            <th class="text-center">
              {{ $t("mediacenter.action") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!files.length">
            <td colspan="6">
              <div class="text-center pa-5">
                <h4>{{ $t("mediacenter.drop-upload") }}</h4>
              </div>
            </td>
          </tr>
          <tr
            v-for="file in files"
            :key="file.id"
          >
            <td class="col-thumb">
              <v-img
                v-if="file.thumb"
                :lazy-src="file.thumb"
                :src="file.thumb"
                width="40"
                height="auto"
              />
              <v-icon v-else>mdi-file</v-icon>
            </td>
            <td class="text-left">
              <div class="filename">
                {{ file.name }}
              </div>
            </td>
            <td>{{ file.size | formatSize }}</td>

            <td v-if="file.error">
              {{ file.error }}
            </td>
            <td v-else-if="file.success">
              {{ $t("mediacenter.success") }}
            </td>
            <td v-else-if="file.active">
              {{ $t("mediacenter.active") }}
            </td>
            <td v-else />
            <td>
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    v-on="on"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    :class="{
                      'dropdown-item': true,
                      disabled: file.active || file.success || file.error === 'compressing',
                    }"
                    href="#"
                    @click.prevent="
                      file.active || file.success || file.error === 'compressing' ? false : onEditFileShow(file)
                    "
                  >
                    <v-list-item-title>
                      {{ $t("mediacenter.edit") }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    class="dropdown-item"
                    href="#"
                    @click.prevent="$refs.upload.remove(file)"
                  >
                    <v-list-item-title>
                      {{ $t("mediacenter.delete") }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-container
        class="pa-0"
        fluid
      >
        <v-row>
          <v-col
            cols="12"
            class="text-right"
          >
            <file-upload
              ref="upload"
              v-model="files"
              :accept="accept"
              :multiple="multiple"
              :size="size || 0"
              :thread="thread < 1 ? 1 : thread > 5 ? 5 : thread"
              :headers="headers"
              :drop="drop"
              :drop-directory="dropDirectory"
              @input-filter="inputFilter"
              @input-file="inputFile"
            />
            <v-btn
              color="primary"
              width="160px"
              class="mr-2"
              :disabled="isShared"
              @click="onAddFile"
            >
              <v-icon class="mr-2">mdi-file-plus</v-icon>
              {{ $t("mediacenter.file") }}
            </v-btn>
            <v-btn
              v-if="!$refs.upload || !$refs.upload.active"
              color="success"
              width="160px"
              :disabled="files.length == 0 || isShared"
              @click.prevent="uploadFile()"
            >
              <v-icon class="mr-2">mdi-cloud-upload</v-icon>
              {{ $t("mediacenter.upload") }}
            </v-btn>
            <v-btn
              v-else
              color="error"
              width="160px"
              @click.prevent="$refs.upload.active = false"
            >
              <v-icon class="mr-2">mdi-stop</v-icon>
              {{ $t("mediacenter.abort") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-dialog
      id="modal-edit-file"
      v-model="editFile.show"
      :width="620"
    >
      <v-card>
        <v-card-title primary-title>
          <h5>
            {{ $t("mediacenter.edit") }}
          </h5>
          <v-spacer />
          <v-btn
            icon
            :title="$t('dialog.close')"
            @click="editFile.show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-form @submit.prevent="onEditorFile">
            <div class="modal-body">
              <div class="form-group">
                <v-text-field
                  id="name"
                  v-model="editFile.name"
                  :label="$t('mediacenter.filename')"
                  required
                />
              </div>
              <div v-if="editFile.show && editFile.blob && editFile.type && editFile.type.substr(0, 6) === 'image/'">
                <div class="edit-image">
                  <img
                    ref="editImage"
                    :src="editFile.blob"
                  />
                </div>
                <div class="edit-image-tool text-center mt-4">
                  <v-btn
                    color="primary"
                    title="cropper.rotate(-90)"
                    class="mr-2"
                    @click="editFile.cropper.rotate(-90)"
                  >
                    <v-icon>mdi-undo</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    title="cropper.rotate(90)"
                    class="mr-2"
                    @click="editFile.cropper.rotate(90)"
                  >
                    <v-icon>mdi-redo</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    title="cropper.crop()"
                    class="mr-2"
                    @click="editFile.cropper.scaleX(-editFile.cropper.getData().scaleX)"
                  >
                    <v-icon>mdi-flip-horizontal</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    title="cropper.crop()"
                    class="mr-2"
                    @click="editFile.cropper.scaleY(-editFile.cropper.getData().scaleY)"
                  >
                    <v-icon>mdi-flip-vertical</v-icon>
                  </v-btn>
                  <v-btn
                    color="primary"
                    :title="$t('dialog.cancel')"
                    @click="editFile.cropper.clear()"
                  >
                    <v-icon>mdi-cancel</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="
              onEditorFile();
              editFile.show = false;
            "
          >
            {{ $t("dialog.ok") }}
          </v-btn>
          <v-btn
            text
            @click="editFile.show = false"
          >
            {{ $t("dialog.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// Libs
import Cropper from "cropperjs";
import FileUpload from "vue-upload-component";
import ImageCompressor from "image-compressor.js";

// Tools
const ft = require("../Tools/FileTools");

export default {
  components: {
    FileUpload,
  },
  filters: {
    formatSize: function (size) {
      if (size > 1024 * 1024 * 1024 * 1024) {
        return (size / 1024 / 1024 / 1024 / 1024).toFixed(1) + " To";
      } else if (size > 1024 * 1024 * 1024) {
        return (size / 1024 / 1024 / 1024).toFixed(1) + " Go";
      } else if (size > 1024 * 1024) {
        return (size / 1024 / 1024).toFixed(1) + " Mo";
      } else if (size > 1024) {
        return (size / 1024).toFixed(1) + " Ko";
      } else if (size > 1) {
        return size.toFixed(1) + " octets";
      }
      return " octet";
    },
  },
  props: {
    currentDirectory: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      files: [],
      accept: "image/*,video/mp4,video/webm,application/pdf,application/zip",
      minSize: 1024,
      size: 1024 * 1024 * 10,
      multiple: true,
      drop: true,
      dropDirectory: false,
      thread: 3,
      name: "file",
      autoCompress: 1024 * 1024,
      editFile: {
        show: false,
        name: "",
      },
    };
  },
  computed: {
    // header changed by the parent according to current dir property
    headers() {
      return { dir: this.currentDirectory };
    },
    isShared() {
      // CHECK IF THE CURRENT DIR IS SHARED
      let shared = false;

      // IF OBJECT CONTAINS SOMETHING CHECK IF IT INCLUDES "SHARE"
      if (this.currentDirectory.length > 0) {
        if (this.currentDirectory[0].includes("mediacenter/share")) {
          shared = true;
        }
      }

      return shared;
    },
  },
  watch: {
    // TODO: OMG
    "editFile.show"(newValue, oldValue) {
      // error
      if (!newValue && oldValue) {
        this.$refs.upload.update(this.editFile.id, {
          error: this.editFile.error || "",
        });
      }

      if (newValue) {
        this.$nextTick(function () {
          if (!this.$refs.editImage) {
            return;
          }

          const cropper = new Cropper(this.$refs.editImage, {
            autoCrop: false,
          });

          this.editFile = {
            ...this.editFile,
            cropper,
          };
        });
      }
    },
  },
  methods: {
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Filter system files or hide files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent();
        }

        // Filter php html js file
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent();
        }

        // Automatic compression
        if (
          newFile.file &&
          newFile.error === "" &&
          newFile.type.substr(0, 6) === "image/" &&
          this.autoCompress > 0 &&
          this.autoCompress < newFile.size
        ) {
          newFile.error = this.$t("mediacenter.compressing");

          const imageCompressor = new ImageCompressor(null, {
            convertSize: 1024 * 1024,
            maxWidth: 512,
            maxHeight: 512,
          });

          imageCompressor
            .compress(newFile.file)
            .then((file) => {
              this.$refs.upload.update(newFile, {
                error: "",
                file,
                size: file.size,
                type: file.type,
              });
            })
            .catch((err) => {
              this.$refs.upload.update(newFile, {
                error: err.message || "compress",
              });
            });
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        // Create a blob field
        newFile.blob = "";
        let URL = window.URL || window.webkitURL;
        if (URL && URL.createObjectURL) {
          newFile.blob = URL.createObjectURL(newFile.file);
        }

        // Thumbnails
        newFile.thumb = "";
        if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
          newFile.thumb = newFile.blob;
        }
      }
    },
    // add, update, remove File Event
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        // update
        if (newFile.active && !oldFile.active) {
          if (newFile.size >= 0 && this.minSize > 0 && newFile.size < this.minSize) {
            this.$refs.upload.update(newFile, { error: "size" });
          }
        }

        if (newFile.progress !== oldFile.progress) {
          // progress
        }

        if (newFile.error && !oldFile.error) {
          // error
        }

        if (newFile.success && !oldFile.success) {
          // success
        }
      }
    },
    onEditFileShow(file) {
      this.editFile = { ...file, show: true };
      this.$refs.upload.update(file, { error: "edit" });
    },
    onEditorFile() {
      if (!this.$refs.upload.features.html5) {
        this.alert("Your browser does not support html5");
        this.editFile.show = false;
        return;
      }

      let data = {
        name: this.editFile.name,
      };

      if (this.editFile.cropper) {
        let binStr = atob(this.editFile.cropper.getCroppedCanvas().toDataURL(this.editFile.type).split(",")[1]);
        let arr = new Uint8Array(binStr.length);
        for (let i = 0; i < binStr.length; i++) {
          arr[i] = binStr.charCodeAt(i);
        }
        data.file = new File([arr], data.name, { type: this.editFile.type });
        data.size = data.file.size;
      }
      this.$refs.upload.update(this.editFile.id, data);
      this.editFile.error = "";
      this.editFile.show = false;
    },
    onAddFile() {
      let input = this.$refs.upload.$el.querySelector("input");
      input.click();
    },
    uploadFile() {
      if (this.currentDirectory.length > 0) {
        if (!this.isShared) {
          const formData = new FormData();

          // Must be the first args
          formData.append("destPath", this.currentDirectory[0]);

          // Append all files in the formdata
          for (const el of this.files) {
            if (el.file instanceof Blob) {
              const file = new File([el.file], el.name, { type: el.type });
              formData.append("files", file);
            } else {
              formData.append("files", el.file);
            }
          }

          // Start upload using filetool upload function
          ft.upload(formData)
            .then(() => {
              // On success clear files array
              this.files = [];

              // Show success message in dialog
              this.$store.dispatch("openSnackbar", {
                //title: this.$t("dialog.info"),
                txt: this.$t("dialog.upload-success"),
                colorBg: "green",
              });
            })
            .catch((err) => {
              // On error show error in dialog
              this.$store.dispatch("openSnackbar", {
                title: this.$t("dialog.warning"),
                txt: this.$t("error.upload"),
                colorBg: "red",
              });
              console.error(err);
            });
        }
      } else {
        console.warn("Current directory not defined");
      }
    },
  },
};
</script>

<style>
@import url("https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.5.6/cropper.min.css");

.upload > .v-data-table th,
.upload > .v-data-table td {
  text-align: center;
}

.upload > .v-data-table .col-thumb {
  width: 75px;
}

.edit-image img {
  max-width: 100%;
  max-height: 400px;
}

.cropper-bg {
  background-repeat: repeat;
}
</style>
